"use client";

import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Separator } from "./ui/separator";
import { Switch } from "./ui/switch";
import { Cookie, Settings, Shield, X } from "lucide-react";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "@/config/site";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
}

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true - required for basic functionality
    analytics: false,
    functional: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      // Show banner after a short delay to avoid disrupting page load
      setTimeout(() => setShowBanner(true), 2000);
    } else {
      // Load existing preferences
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
        applyCookieSettings(savedPreferences);
      } catch (error) {
        console.error("Error parsing cookie preferences:", error);
      }
    }
    // Runs once on mount to restore saved preferences.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyCookieSettings = (prefs: CookiePreferences) => {
    // Apply analytics settings
    if (prefs.analytics && typeof window !== "undefined") {
      // Initialize Google Analytics
      initializeGoogleAnalytics();
    }

    // Apply functional cookies settings
    if (prefs.functional) {
      // Enable functional cookies like language preferences, etc.
    }

    // Apply marketing cookies settings
    if (prefs.marketing) {
      // Enable marketing/advertising cookies
    }
  };

  const initializeGoogleAnalytics = () => {
    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;

    if (!measurementId) {
      console.error("GA Measurement ID is missing");
      return;
    }

    // Avoid adding GA script multiple times
    if (document.getElementById("ga-script")) {
      return;
    }

    // Load gtag.js
    const script = document.createElement("script");
    script.id = "ga-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize GA after script loads
    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      // gtag.js only reads the `arguments` object; pushing a plain array is ignored.
      function gtag(..._args: unknown[]) {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", measurementId, {
        anonymize_ip: true, // GDPR-friendly
      });
    };
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true,
    };

    setPreferences(allAccepted);
    localStorage.setItem("cookie-consent", JSON.stringify(allAccepted));
    localStorage.setItem("cookie-consent-date", new Date().toISOString());
    applyCookieSettings(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptNecessaryOnly = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false,
    };

    setPreferences(necessaryOnly);
    localStorage.setItem("cookie-consent", JSON.stringify(necessaryOnly));
    localStorage.setItem("cookie-consent-date", new Date().toISOString());
    applyCookieSettings(necessaryOnly);
    setShowBanner(false);
    setShowSettings(false);
  };

  const saveCustomPreferences = () => {
    localStorage.setItem("cookie-consent", JSON.stringify(preferences));
    localStorage.setItem("cookie-consent-date", new Date().toISOString());
    applyCookieSettings(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const updatePreference = (key: keyof CookiePreferences, value: boolean) => {
    if (key === "necessary") return; // Cannot disable necessary cookies

    setPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Banner */}
      {!showSettings && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
          <div className="max-w-7xl mx-auto p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start space-x-3 flex-1">
                <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                  <Cookie className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 mb-1">
                    We use cookies
                  </h3>
                  <p className="text-sm text-gray-600">
                    We use cookies to enhance your experience, analyze site
                    traffic, and provide personalized content. You can manage
                    your preferences or learn more in our{" "}
                    <a
                      href="/privacy"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSettings(true)}
                  className="w-full sm:w-auto"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Customize
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={acceptNecessaryOnly}
                  className="w-full sm:w-auto"
                >
                  Essential Only
                </Button>
                <Button
                  size="sm"
                  onClick={acceptAll}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                >
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-blue-600" />
                  <CardTitle>Cookie Preferences</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSettings(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Manage your cookie preferences. You can enable or disable
                different types of cookies below.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Necessary Cookies */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      Necessary Cookies
                    </h4>
                    <p className="text-sm text-gray-600">
                      Essential for the website to function properly. Cannot be
                      disabled.
                    </p>
                  </div>
                  <Switch checked={preferences.necessary} disabled={true} />
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600">
                  <strong>Purpose:</strong> Authentication, security, and basic
                  website functionality
                  <br />
                  <strong>Data:</strong> Session tokens, security preferences
                  <br />
                  <strong>Retention:</strong> Session-based or as needed for
                  functionality
                </div>
              </div>

              <Separator />

              {/* Analytics Cookies */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      Analytics Cookies
                    </h4>
                    <p className="text-sm text-gray-600">
                      Help us understand how visitors use our website to improve
                      user experience.
                    </p>
                  </div>
                  <Switch
                    checked={preferences.analytics}
                    onCheckedChange={(checked) =>
                      updatePreference("analytics", checked)
                    }
                  />
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600">
                  <strong>Purpose:</strong> Website analytics, performance
                  monitoring
                  <br />
                  <strong>Data:</strong> Page views, user interactions,
                  demographic data
                  <br />
                  <strong>Retention:</strong> Up to 26 months
                  <br />
                  <strong>Third Parties:</strong> Google Analytics
                </div>
              </div>

              <Separator />

              {/* Functional Cookies */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      Functional Cookies
                    </h4>
                    <p className="text-sm text-gray-600">
                      Remember your preferences and settings to enhance your
                      experience.
                    </p>
                  </div>
                  <Switch
                    checked={preferences.functional}
                    onCheckedChange={(checked) =>
                      updatePreference("functional", checked)
                    }
                  />
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600">
                  <strong>Purpose:</strong> User preferences, language settings,
                  UI customizations
                  <br />
                  <strong>Data:</strong> Theme preferences, language choices,
                  form data
                  <br />
                  <strong>Retention:</strong> Up to 12 months
                </div>
              </div>

              <Separator />

              {/* Marketing Cookies */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      Marketing Cookies
                    </h4>
                    <p className="text-sm text-gray-600">
                      Used to deliver relevant advertisements and measure their
                      effectiveness.
                    </p>
                  </div>
                  <Switch
                    checked={preferences.marketing}
                    onCheckedChange={(checked) =>
                      updatePreference("marketing", checked)
                    }
                  />
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-600">
                  <strong>Purpose:</strong> Targeted advertising, conversion
                  tracking
                  <br />
                  <strong>Data:</strong> Ad interactions, conversion events,
                  remarketing data
                  <br />
                  <strong>Retention:</strong> Up to 24 months
                  <br />
                  <strong>Third Parties:</strong> Google Ads, Facebook Pixel
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={acceptNecessaryOnly}
                  className="w-full sm:w-auto"
                >
                  Essential Only
                </Button>
                <Button
                  onClick={saveCustomPreferences}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                >
                  Save Preferences
                </Button>
              </div>

              <div className="text-xs text-gray-500 pt-4 border-t">
                <p>
                  <strong>Your Rights:</strong> You have the right to withdraw
                  consent at any time. You can change your preferences by
                  clicking the cookie settings in our footer.
                </p>
                <p className="mt-2">
                  <strong>Contact:</strong> For questions about our use of
                  cookies, contact us at{" "}
                  <a
                    href={SUPPORT_MAILTO}
                    className="text-blue-600 underline"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
